import '../../css/PerformanceClause.css';
import ContentBox from './ContentBox';

export default function PerformanceClause() {
  return (
    <ContentBox>

      <main className="pc-box" role="main">

        <h1 className="pc-h1">About</h1>
        <h2 className="pc-h2">We make Meta billions without compensation.</h2>

        {/* KPIs */}
        <section className="pc-kpis" aria-label="Key figures">
          <article className="pc-kpi">
            <div className="pc-kpi-label">Instagram revenue (2024)</div>
            <div className="pc-kpi-value">$71B</div>
          </article>
          <article className="pc-kpi">
            <div className="pc-kpi-label">Instagram ad reach</div>
            <div className="pc-kpi-value">1.74B / year</div>
            <div className="pc-kpi-sub">Over 21% of the human population</div>
          </article>
        </section>

        {/* Body copy */}
        <p style={{marginTop: 0}}>
          We, the users, make these profits possible.
        </p>
        <p style={{marginTop: 0}}>
          Every post, every scroll, every like is labour that feeds Meta’s business model: more ad space, more data, more profit. Meta pays nothing for that labour.
        </p>
        <p style={{marginTop: 0}}>
          Meta Invoice takes the visible traces of your activity — posts, time, audience — and turns them into an invoice for the estimated value you’ve created for Meta.
        </p>
        
        <h2 className="pc-h2">Scrolling costs more than just time.</h2>

        <section className="pc-kpis" aria-label="Key figures">
          <article className="pc-kpi">
            <div className="pc-kpi-label">Total Time Spent on Instagram Daily</div>
            <div className="pc-kpi-value">1.13B Hours</div>
            <div className="pc-kpi-sub">How long it would take to cycle around the Earth 412,000 times</div>
          </article>
          <article className="pc-kpi">
            <div className="pc-kpi-label">Percent of Global Young Adults Reporting Loneliness</div>
            <div className="pc-kpi-value">19%</div>
            <div className="pc-kpi-sub">Studies show social media as a key contributing factor</div>
          </article>
        </section>

        <p style={{marginTop: 0}}>
          The World Happiness Report links Instagram use to lower life satisfaction and greater loneliness. These effects are strongest among teens and heavy users, and controlled studies confirm that reducing or quitting improves well-being.
        </p>
        
        <p style={{marginTop: 0}}>
          It’s not just the cost to individuals - it's the norms that make us human slipping quietly through our scrolling fingers. The erosion of connection, attention, and trust.
        </p>

        <p className="pc-impact">
          Meta Invoice bills Instagram for these damages.
        </p>


        <h2 className="pc-h2">The Math</h2>

        <p style={{marginTop: 0}}>
          Your invoice is built from four parts: the content you post, the time you scroll, the labour you put in, and the hidden social cost of the platform.
        </p>
        
      
        <h2 className="pc-subhead">Calculation Logic</h2>
        
        <ul style={{marginTop: 0, listStyle: 'numbered'}}>
          
          <li>
            <span className="pc-strong">Content Revenue</span><br></br>
            Followers × 3 × 48 ÷ 1,000 × $19.80 = Content Revenue
          </li>

          <li>
            <span className="pc-strong">Doomscroll Time Value</span><br></br>
            (Daily Minutes ÷ 60) × 1,460 × $0.34 = Doomscroll Time Value
          </li>

          <li>
            <span className="pc-strong">Creative Labour Value</span><br></br>
            (Grid Posts ÷ 0.615) × Minutes per Post × (Hourly Rate ÷ 60) = Creative Labour Value
          </li>
          
          <li>
            <span className="pc-strong">Wellbeing Damages</span><br></br>
            Flat $6000 fee on every invoice
          </li>
        </ul>

        <h3  className="pc-h3" style={{marginBottom: 0}}>
          Content Revenue
        </h3>

        <p>
          Every post creates ad space. Advertisers pay Meta for the eyes your posts attract. Meta earns on average <b>$19.80 per 1,000 impressions</b>, so we calculate your impressions using a conservative, research-backed multiplier of <b>Followers × 3 × 12</b>. We then divide by 1,000 and multiply by $19.80. This turns your attention into Meta's revenue.
        </p>
        <h3  className="pc-h3" style={{marginBottom: 0}}>
          Doomscroll Time Value
        </h3>

        <p>
          Even when you're not posting, your attention generates profit. Based on Meta's annual revenue of <b>$49.63 per user</b>, and an average usage of <b>12 hours per month</b>, Meta earns roughly <b>$0.34 per hour of your time</b>. Over four years (the average account age), we convert your daily screen time into the total dollar value of your scrolling.
        </p>

        <h3  className="pc-h3" style={{marginBottom: 0}}>
          Creative Labour Value
        </h3>

        <p>
          Behind every post lies unseen creative labour: shooting, editing, curating a digital identity. We take your visible grid post count, divide it by <b>0.615</b> to estimate your total content including Reels, Lives, and Stories, then multiply by your minutes per post and chosen hourly rate. It's an equitable wage for unpaid creative labour that keeps Instagram profitable.
        </p>
        <h3  className="pc-h3" style={{marginBottom: 0}}>
          Wellbeing Damages
        </h3>

        <p>
          Instagram's costs aren't only economic. Studies show social media use lowers life satisfaction by <b>0.1 points per year</b> on the <b>Global Happiness Index</b>. We estimate that improving happiness by one full point is worth around <b>$15,000 per year</b>, meaning Instagram's 0.1-point annual drop costs about <b>$1,500 per user, per year</b>. Across four years, that’s $6000 - a flat, research-backed fee we include on every invoice to charge Instagram for the wellbeing it erodes.
        </p>

        <p className="pc-impact">
          Together, these four components form Your Invoice Total.
        </p>
        
        <h4 className="pc-h4">Make your time pay.</h4>

        <div className="pc-cta" style={{width: '100%'}}>
          <button className="pc-button" style={{width: '100%'}} onClick={() => (window.location.href = '/form')}>
            Generate Your Invoice
          </button>
        </div>


        <div style={{fontSize: 13}}>

        
        <h2 className="pc-h2">References</h2>

        <p>
            Backlinko. (2025). Instagram Statistics: Key Demographic and User Numbers. <a href='https://backlinko.com/instagram-users' style={{wordBreak: 'break-all'}} target='_blank'>https://backlinko.com/instagram-users</a>
        </p>
        
        <p>
          Gallup. (2024). Gallup World Poll: Well-being and Social Data. <a target='_blank' style={{wordBreak: 'break-all'}}  href='https://www.gallup.com/analytics/247355/gallup-world-happiness-report.aspx'>https://www.gallup.com/analytics/247355/gallup-world-happiness-report.aspx</a>
        </p>

        <p>
          Layard, R. et al. (2022). World Happiness Report. Sustainable Development Solutions Network, Oxford Wellbeing Research Centre.  <a target='_blank'  style={{wordBreak: 'break-all'}} href='https://www.worldhappiness.report/'>https://www.worldhappiness.report/</a>
        </p>

        <p>
          McLean Hospital. (2025, July 4). How Social Media Affects Mental Health. <a target='_blank'  style={{wordBreak: 'break-all'}} href='https://www.mcleanhospital.org/essential/social-media'>https://www.mcleanhospital.org/essential/social-media</a>
        </p>

        <p>
          Measure Studio. (2025, July 4). 47 Surprising Instagram Statistics for 2025. <a target='_blank'  style={{wordBreak: 'break-all'}} href='https://www.measure.studio/post/instagram-statistics'>https://www.measure.studio/post/instagram-statistics</a>
        </p>

        <p>
          Sprout Social. (2025, June 19). 26 Instagram stats you need to know for 2025. <a target='_blank'  style={{wordBreak: 'break-all'}} href='https://sproutsocial.com/insights/instagram-stats/'>https://sproutsocial.com/insights/instagram-stats/</a>
        </p>

        <p>
          Statista. (2024). Instagram users worldwide 2025. <a target='_blank'  style={{wordBreak: 'break-all'}} href='https://www.statista.com/statistics/183585/instagram-number-of-global-users/'>https://www.statista.com/statistics/183585/instagram-number-of-global-users/</a>
        </p>

        <p>
          Taylor Gage. (2025, Jan 29). Browsing for Happiness: How Instagram Culture is Making Us Unwell. <a target='_blank' style={{wordBreak: 'break-all'}}  href='https://taylorgage.com/blog/browsing-for-happiness'>https://taylorgage.com/blog/browsing-for-happiness</a>
        </p>

        <p>
          UCDavis Health. (2024, May 9). Social media's impact on our mental health and tips to use it safely. <a target='_blank'  style={{wordBreak: 'break-all'}} href='https://health.ucdavis.edu/blog/cultivating-health/social-medias-impact-our-mental-health-and-tips-to-use-it-safely/2024/05'>https://health.ucdavis.edu/blog/cultivating-health/social-medias-impact-our-mental-health-and-tips-to-use-it-safely/2024/05</a>
        </p>

        <p>
          World Happiness Report. (2024). Frequently Asked Questions. <a target='_blank'  style={{wordBreak: 'break-all'}} href='https://www.worldhappiness.report/faq/'>https://www.worldhappiness.report/faq/</a>
        </p>

        <p>
          World Happiness Report. (2024). Connecting with others: How social connections improve the happiness of young adults. <a target='_blank'  style={{wordBreak: 'break-all'}} href='https://www.worldhappiness.report/ed/2025/connecting-with-others-how-social-connections-improve-the-happiness-of-young-adults/'>https://www.worldhappiness.report/ed/2025/connecting-with-others-how-social-connections-improve-the-happiness-of-young-adults/</a>
        </p>         
        </div>
      </main>
    </ContentBox>
  );
}